import { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { API } from "../hooks/useApi";
import { readCsrfCookie } from "../context/AuthContext";

export function AskMrE({ bookId, bookTitle, page }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [restoredFromHistory, setRestoredFromHistory] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API}/ai/mre/history/${bookId}`, { credentials: "include" });
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (cancelled) return;
        if (data.session_id && data.messages?.length) {
          setSessionId(data.session_id);
          setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
          setRestoredFromHistory(true);
        }
      } catch {
        // best-effort — a fresh chat still works if history can't load
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (text) => {
    if (!text.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const resp = await fetch(`${API}/ai/mre/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": readCsrfCookie() || "" },
        body: JSON.stringify({ book_id: bookId, message: text, page: page || 1, session_id: sessionId || undefined }),
      });
      if (!resp.ok || !resp.body) {
        const body = await resp.json().catch(() => null);
        setMessages((m) => [...m, { role: "assistant", content: body?.detail || "Mr.E is unavailable right now. Please try again." }]);
        setLoading(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      setMessages((m) => [...m, { role: "assistant", content: "" }]);
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.type === "delta") assistantText += payload.content;
          if (payload.type === "error") assistantText = assistantText || payload.content;
          if (payload.type === "done" && payload.session_id) setSessionId(payload.session_id);
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: assistantText };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Mr.E is unavailable right now. Please try again." }]);
    }
    setLoading(false);
  };

  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  return (
    <>
      <button
        data-testid="ask-mre-open-button"
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 right-6 z-[60] bg-white text-black px-4 py-3 text-xs uppercase tracking-wide flex items-center gap-2 shadow-lg hover:bg-white/85 transition-opacity duration-200 ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <Sparkles className="w-4 h-4" /> Ask Mr.E
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center md:justify-end bg-black/60" onClick={() => setOpen(false)}>
          <div
            data-testid="ask-mre-panel"
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0A0A0A] border border-white/15 w-full md:w-[420px] md:mr-6 h-[75vh] md:h-[640px] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brandAccent" />
                <p className="text-sm uppercase tracking-wide truncate max-w-[260px]">Ask Mr.E — {bookTitle}</p>
              </div>
              <button data-testid="ask-mre-close-button" onClick={() => setOpen(false)}>
                <X className="w-4 h-4 opacity-60 hover:opacity-100" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {!historyLoaded && (
                <p data-testid="ask-mre-history-loading" className="text-xs text-white/40">Loading your past conversation…</p>
              )}
              {historyLoaded && messages.length === 0 && (
                <p data-testid="ask-mre-empty-state" className="text-xs text-white/60">
                  Ask about page {page} of {bookTitle}. Mr.E only answers from this book's authorized content.
                </p>
              )}
              {historyLoaded && messages.length > 0 && restoredFromHistory && (
                <p data-testid="ask-mre-history-restored" className="text-[10px] uppercase tracking-wide text-white/35 text-center">Continuing your earlier conversation</p>
              )}
              {messages.map((m, i) => (
                <div key={i} data-testid={`ask-mre-message-${m.role}-${i}`} className={m.role === "user" ? "text-right" : ""}>
                  <div className={`inline-block max-w-[90%] px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-white text-black" : "bg-white/5 border border-white/10"}`}>
                    {m.content || (loading && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2 p-4 border-t border-white/10">
              <input
                data-testid="ask-mre-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Ask about page ${page}…`}
                maxLength={600}
                className="flex-1 bg-white/5 border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-white/30"
              />
              <button data-testid="ask-mre-send-button" type="submit" disabled={loading} className="bg-white text-black px-3 py-2 disabled:opacity-40">
                <Send className="w-4 h-4" />
              </button>
            </form>
            <p className="text-[10px] text-white/45 px-4 pb-3">Educational only — grounded in this book's authorized text.</p>
          </div>
        </div>
      )}
    </>
  );
}
